import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import { PageHeader, StateBlock, Skeleton } from "../../components/UI";
import Dialog from "../../components/UI/Dialog";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFormat } from "../../hooks/useFormat";
import { apiFetch } from "../../lib/api";
import { Action } from "../MVP/shared";
import { Chip, Rows, Surface, Toolbar, adminErrorText } from "./ui";

type C = { id: string; userId: string; subject: string; body: string; status: string; createdAt: string; reasonCode: string };
const FILTERS = ["", "open", "in_progress", "closed"] as const;

const List = styled(Rows)`
  > li {
    grid-template-columns: minmax(0, 1fr) auto auto;
  }
  @media (max-width: 640px) {
    > li {
      grid-template-columns: minmax(0, 1fr) auto;
    }
    .date {
      display: none;
    }
  }
`;
const Seg = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: ${(p) => p.theme.colors.input};
  button {
    min-height: 40px;
    padding: 0 14px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textSecondary};
    &[aria-pressed="true"] {
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.text};
    }
  }
`;
const Body = styled.div`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  padding: 16px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.navigation};
  font-size: 15px;
  line-height: 22px;
`;

const tone = (s: string) => (s === "open" ? "warn" : s === "in_progress" ? "info" : "muted");
const label = (t: (k: string) => string, s: string) => (s === "open" ? t("adm.mod_open") : s === "in_progress" ? t("adm.mod_progress") : t("adm.mod_closed"));

export default function Moderation() {
  const { t } = useLanguage();
  const f = useFormat();
  const [items, setItems] = useState<C[] | null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<(typeof FILTERS)[number]>("");
  const [pick, setPick] = useState<C | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const params = new URLSearchParams({ page: "1", pageSize: "100" });
      if (status) params.set("status", status);
      const res = await apiFetch(`/admin/complaints?${params}`);
      if (!res.ok) throw new Error(String(res.status));
      const d = await res.json();
      setItems(d.items || []);
    } catch (e) {
      setItems(null);
      setError((e as Error).message || "error");
    }
  }, [status]);
  useEffect(() => {
    setItems(null);
    void load();
  }, [load]);

  async function setTo(c: C, next: string) {
    if (busy) return;
    setBusy(true);
    try {
      const res = await apiFetch(`/admin/complaints/${encodeURIComponent(c.id)}`, { method: "PATCH", body: JSON.stringify({ status: next }) });
      if (!res.ok) throw new Error();
      toast.success(t("adm.mod_updated"));
      setPick(null);
      await load();
    } catch {
      // Показываем фактическое состояние: возможно, запись уже обработана.
      toast.error(t("adm.load_error"));
      await load();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title={t("adm.moderation")} description={t("adm.mod_note")} actions={<Action type="button" onClick={load}>{t("adm.refresh")}</Action>} />
      <Surface aria-label={t("adm.moderation")}>
        <Toolbar>
          <Seg role="group" aria-label={t("adm.mod_filter")}>
            {FILTERS.map((s) => (
              <button key={s || "all"} type="button" aria-pressed={status === s} onClick={() => setStatus(s)}>
                {s ? label(t, s) : t("adm.mod_all")}
              </button>
            ))}
          </Seg>
          {items && <span className="count">{t("adm.total", { n: items.length })}</span>}
        </Toolbar>
        {error ? (
          <StateBlock kind="error" title={t("adm.load_error")} text={adminErrorText(error, t)} actionLabel={t("adm.retry")} onAction={load} />
        ) : !items ? (
          <div role="status" aria-label={t("adm.loading")} style={{ display: "grid", gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} $h={60} $r={10} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <StateBlock kind="empty" title={t("adm.mod_empty")} />
        ) : (
          <List>
            {items.map((c) => (
              <li key={c.id}>
                <div style={{ minWidth: 0 }}>
                  <div className="name">{c.subject}</div>
                  <div className="sub">
                    {c.reasonCode ? `${c.reasonCode} · ` : ""}
                    <span className="date">{f.date(c.createdAt)}</span>
                  </div>
                </div>
                <Chip $tone={tone(c.status)}>{label(t, c.status)}</Chip>
                <Action type="button" $variant="ghost" onClick={() => setPick(c)} aria-label={`${t("adm.details")}: ${c.subject}`}>
                  {t("adm.details")}
                </Action>
              </li>
            ))}
          </List>
        )}
      </Surface>
      {pick && (
        <Dialog
          size="md"
          label={pick.subject}
          onClose={() => setPick(null)}
          footer={
            <>
              <Action type="button" onClick={() => setPick(null)} disabled={busy}>
                {t("adm.close")}
              </Action>
              {pick.status === "open" && (
                <Action type="button" onClick={() => setTo(pick, "in_progress")} disabled={busy}>
                  {t("adm.mod_to_progress")}
                </Action>
              )}
              {pick.status !== "closed" && (
                <Action type="button" $variant="primary" onClick={() => setTo(pick, "closed")} disabled={busy} aria-busy={busy}>
                  {t("adm.mod_close")}
                </Action>
              )}
            </>
          }
        >
          <h2>{pick.subject}</h2>
          <p className="sub" style={{ fontSize: 14, opacity: 0.8 }}>
            <Chip $tone={tone(pick.status)}>{label(t, pick.status)}</Chip> {f.date(pick.createdAt)} · {t("adm.mod_from", { id: pick.userId })}
            {pick.reasonCode ? ` · ${t("adm.mod_reason")}: ${pick.reasonCode}` : ""}
          </p>
          <Body>{pick.body}</Body>
        </Dialog>
      )}
    </>
  );
}
